import { ProjectStatusIcon } from "~/components/icons/project-status"
import { useLocation } from "react-router-dom"
import { toast } from "sonner"
import { GraduationCapIcon } from "lucide-react"
import { TutorialContext } from "~/components/tutorials/context"
import { OnboardingStep } from "~/components/tutorials/OnboardingStep"
import { useProjectPractice } from "~/hooks/useProjectPractice"
import { useTutorialAnalytics } from "~/hooks/useTutorialAnalytics"
import { tutorials } from "~/lib/tutorials"
import ProjectDetailsPage from "./project-details"
import { FolderKanbanIcon, PlusIcon } from "lucide-react"
import { useContext, useEffect, useRef, useState } from "react"
import { format } from "date-fns"
import { Link } from "~/components/Link"
import { AppHeader } from "~/components/AppHeader"
import { EntityIdentity } from "~/components/EntityIdentity"
import { IssueSetting } from "~/components/issueDetailsPage/IssueSetting"
import {
	EmptyState,
	ErrorState,
	LoadingState
} from "~/components/screens/StateScreen"
import { Button } from "~/components/ui/button"
import { Badge } from "~/components/ui/badge"
import { Progress } from "~/components/ui/progress"
import {
	Table,
	TableBody,
	TableCell,
	TableHead,
	TableHeader,
	TableRow,
	TableRowLink
} from "~/components/ui/table"
import { useTeam } from "~/hooks/useTeam"
import { usePermission } from "~/hooks/usePermission"
import { ProjectStatus, projectStatusValues } from "~/lib/enums"
import { TeamPermissions } from "~/lib/permissions"
import { enumFormat } from "~/lib/utils"
import { api } from "~/trpc/react"

function LiveProjects() {
	const coordinator = useContext(TutorialContext)
	const { team, members, isLoading, orgSlug } = useTeam()
	const { hasTeamPermission } = usePermission()
	const utils = api.useUtils()
	const [status, setStatus] = useState<ProjectStatus | null>(null)
	const [lead, setLead] = useState<string | null>(null)
	const projects = api.projects.list.useQuery(
		{
			teamId: team?.id ?? "",
			status: status ?? undefined,
			leadId: lead === "unassigned" ? null : (lead ?? undefined)
		},
		{ enabled: !!team?.enableProjects }
	)
	api.issues.onUpdate.useSubscription(
		{ teamId: team?.id ?? "" },
		{
			enabled: !!team?.enableProjects,
			onData: () => {
				void utils.projects.list.invalidate()
			}
		}
	)
	if (isLoading) return <LoadingState />
	if (!team?.enableProjects) return <EmptyState title="Projects are disabled" />
	if (projects.isLoading) return <LoadingState />
	if (projects.error)
		return (
			<ErrorState
				title="Unable to load projects"
				description={projects.error.message}
			/>
		)
	if (!team) return <EmptyState title="Team not found" />
	const base = `/${orgSlug}/${team.slug}/projects`
	return (
		<main className="flex h-full min-h-0 flex-col">
			<AppHeader
				contentClassName="flex items-center gap-2"
				actions={
					<>
						<Button
							variant="ghost"
							size="sm"
							onClick={() => coordinator.start("projects-v1")}
						>
							<GraduationCapIcon className="size-4" />
							Tutorial
						</Button>
						{hasTeamPermission(TeamPermissions.EDIT_ISSUES) && (
							<Button asChild>
								<Link to={`${base}/new`}>
									<PlusIcon className="size-4" /> New project
								</Link>
							</Button>
						)}
					</>
				}
			>
				<EntityIdentity kind="team" name={team.name} image={team.logo} />
				<span className="text-text-sub">/</span>
				<span className="font-medium">Projects</span>
			</AppHeader>
			<div className="grid grid-cols-1 gap-3 px-4 py-4 sm:grid-cols-[12rem_14rem] sm:px-6">
				<IssueSetting
					ariaLabel="Filter projects by status"
					value={status}
					onChange={setStatus}
					showArrow
					showBorder
					options={[
						{ value: null, display: "All statuses" },
						...projectStatusValues.map((value) => ({
							value,
							searchText: enumFormat(value),
							display: <ProjectStatusIcon status={value} withText />
						}))
					]}
				/>
				<IssueSetting
					ariaLabel="Filter projects by lead"
					value={lead}
					onChange={setLead}
					showArrow
					showBorder
					withSearch
					options={[
						{ value: null, display: "All leads" },
						{ value: "unassigned", display: "No lead" },
						...(members?.map((member) => ({
							value: member.id,
							searchText: member.orgMember.user.name,
							display: (
								<EntityIdentity
									kind="user"
									userId={member.orgMember.userId}
									name={member.orgMember.user.name}
									image={member.orgMember.user.image}
									size="sm"
								/>
							)
						})) ?? [])
					]}
				/>
			</div>
			<div className="min-h-0 flex-1 overflow-auto px-4 pb-8 sm:px-6">
				{projects.data?.length ? (
					<Table>
						<TableHeader>
							<TableRow>
								<TableHead>Project</TableHead>
								<TableHead>Status</TableHead>
								<TableHead>Lead</TableHead>
								<TableHead>Target</TableHead>
								<TableHead>Progress</TableHead>
							</TableRow>
						</TableHeader>
						<TableBody>
							{projects.data.map((project) => (
								<TableRow key={project.id} linked>
									<TableCell>
										<TableRowLink to={`${base}/${project.id}`}>
											<FolderKanbanIcon className="size-4 shrink-0 text-icons" />
											{project.name}
										</TableRowLink>
									</TableCell>
									<TableCell>
										<Badge variant="secondary">
											<ProjectStatusIcon status={project.status} withText />
										</Badge>
									</TableCell>
									<TableCell>
										{members?.find((member) => member.id === project.leadId)
											?.orgMember.user.name ?? "—"}
									</TableCell>
									<TableCell>
										{project.targetDate
											? format(project.targetDate, "MMM d, yyyy")
											: "—"}
									</TableCell>
									<TableCell>
										<div className="flex min-w-32 flex-col gap-2">
											<span className="text-xs text-text-sub">
												{project.completed}/{project.total} complete
											</span>
											<Progress
												aria-label={`${project.name} completion, excluding canceled issues`}
												value={
													project.total
														? (project.completed / project.total) * 100
														: 0
												}
											/>
										</div>
									</TableCell>
								</TableRow>
							))}
						</TableBody>
					</Table>
				) : (
					<EmptyState
						scope="inline"
						title={
							status || lead
								? "No projects match these filters"
								: "No projects yet"
						}
					/>
				)}
			</div>
		</main>
	)
}

export default function ProjectsPage() {
	const { pending, claim, release, ready } = useContext(TutorialContext)
	const { team } = useTeam()
	const location = useLocation()
	const [active, setActive] = useState(false)
	const owner = useRef(Symbol("projects-v1"))
	useEffect(() => {
		if (
			!ready ||
			!team?.enableProjects ||
			pending?.id !== "projects-v1" ||
			(pending.destination && pending.destination !== location.pathname)
		)
			return
		const token = owner.current
		if (!claim(token)) return
		setActive(true)
		return () => {
			setActive(false)
			release(token)
		}
	}, [
		ready,
		team?.id,
		team?.enableProjects,
		pending?.id,
		pending?.destination,
		location.pathname,
		claim,
		release
	])
	return active && pending?.id === "projects-v1" ? (
		<ProjectsPractice onExit={() => release(owner.current)} />
	) : (
		<LiveProjects />
	)
}

function ProjectsPractice({ onExit }: { onExit: () => void }) {
	const practice = useProjectPractice()
	const [step, setStep] = useState(1)
	const analytics = useTutorialAnalytics("projects-v1", step - 1)
	const utils = api.useUtils()
	const finish = api.user.finishTutorial.useMutation({
		onSuccess: () => {
			void utils.user.getTutorialPreferences.invalidate()
		},
		onError: () =>
			toast.error("Couldn't save tutorial progress. Please try again.")
	})
	const tutorial = tutorials["projects-v1"]
	const task = tutorial.steps[step - 1]!
	const complete =
		task.id === "status"
			? practice.project.status === ProjectStatus.InProgress && practice.canPost
			: task.id === "dates"
				? practice.project.startDate.toDateString() !==
						practice.createdAt.toDateString() &&
					!!practice.project.targetDate &&
					practice.project.targetDate >= practice.project.startDate
				: task.id === "members"
					? practice.participants.some(
							(member) => member.id === "practice-sam" && !member.included
						)
					: task.id === "updates"
						? practice.updates.some(
								(update) => update.id !== "practice-project-update-kevin"
							)
						: task.id === "summary"
							? practice.project.description.trim() !==
								tutorial.practice.description.trim()
							: task.id === "issues"
								? practice.issues.every(
										(issue) => issue.projectId === practice.project.id
									)
								: task.id === "resources"
									? practice.project.resources.some(
											(resource) =>
												resource.title.trim().toLowerCase() ===
													"pilot checklist" &&
												resource.url.replace(/\/$/, "") ===
													"https://biteclub.example/pilot"
										)
									: true
	return (
		<ProjectDetailsPage
			practice={practice}
			tutorialActions={
				<>
					<Badge variant="secondary">Practice mode</Badge>
					<Button
						variant="ghost"
						size="sm"
						disabled={finish.isPending}
						onClick={() => {
							analytics.end("exited", "exit_button")
							onExit()
						}}
					>
						Exit tour
					</Button>
				</>
			}
			tutorialPrompt={
				<OnboardingStep
					step={step}
					total={tutorial.steps.length}
					task={task}
					complete={complete}
					pending={finish.isPending}
					onBack={() => setStep(step - 1)}
					onAdvance={async (skipped) => {
						if (finish.isPending) return
						if (step < tutorial.steps.length) {
							analytics.finishStep(skipped)
							setStep(step + 1)
							return
						}
						try {
							await finish.mutateAsync({
								tutorialId: "projects-v1",
								outcome: "completed"
							})
							analytics.finishStep(skipped)
							analytics.end("completed", "finished")
							toast.success(
								"Projects tour complete. Your Biteclub practice changes have been discarded."
							)
							onExit()
						} catch {
							/* Keep the tour open for retry. */
						}
					}}
				/>
			}
		/>
	)
}
